/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useBranding.ts
 🗓️ Created: 2025-01-06
 🧩 Description:
    Custom hook for managing branding configuration based on domain.
    Fetches branding settings for the current domain and applies them
    to the login page and other components.

 🔧 Notes:
    - Detects current domain from window.location
    - Fetches branding configuration from API
    - Provides fallback to main tenant branding
    - Handles loading and error states
    - Applies branding to document head (favicon, title)

 🔁 Last Updated: 2025-01-06 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { buildApiUrl, getApiHeaders } from '../config/api';

export interface BrandingConfig {
  // Application Information
  appName: string;
  appDescription: string;
  welcomeMessage: string;
  footerText: string;
  primaryColor: string;
  secondaryColor: string;

  // Logos & Icons
  logo?: string;
  logoDark?: string;
  mobileLogo?: string;
  mobileLogoDark?: string;
  favicon?: string;
  faviconDark?: string;

  // Login Design
  loginBackgroundColor?: string;
  loginFontColor?: string;
  loginFormBackground?: string;
  loginSubmitButtonColor?: string;
  loginAddonsMenuColor?: string;
  disableLoginBackgroundImage?: boolean;
  loginBackgroundImage?: string;
  loginLogo?: string;
}

export interface BrandingData {
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  brandingConfig: BrandingConfig;
}

export const useBranding = () => {
  const [brandingData, setBrandingData] = useState<BrandingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get current domain from window.location
  const getCurrentDomain = useCallback(() => {
    if (typeof window === 'undefined') return 'localhost';

    const hostname = window.location.hostname;

    // If it's localhost or an IP address, return as is
    if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return hostname;
    }

    // For other domains, return the full hostname
    return hostname;
  }, []);

  // Fetch branding configuration by domain
  const fetchBranding = useCallback(async (domain: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        buildApiUrl(`/api/branding/by-domain?domain=${encodeURIComponent(domain)}&apiKey=${encodeURIComponent(import.meta.env.VITE_PUBLIC_BRANDING_API_KEY || 'branding-public-2025')}`),
        {
          method: 'GET',
          // No auth headers needed for public endpoint
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch branding configuration');
      }

      const data = await response.json();

      if (data.success && data.brandingConfig) {
        // Convert relative image paths to absolute URLs
        const brandingConfig = data.brandingConfig;
        const rawApiUrl = import.meta.env.VITE_API_URL;
        const apiBaseUrl = (rawApiUrl && rawApiUrl.startsWith('http')) ? rawApiUrl : window.location.origin;

        const resolveImagePath = (path: string | null | undefined) => {
          if (!path) return path;
          if (path.startsWith('http://') || path.startsWith('https://')) return path;
          return `${apiBaseUrl}${path}`;
        };

        const resolvedConfig = {
          ...brandingConfig,
          logo: resolveImagePath(brandingConfig.logo),
          logoDark: resolveImagePath(brandingConfig.logoDark),
          mobileLogo: resolveImagePath(brandingConfig.mobileLogo),
          mobileLogoDark: resolveImagePath(brandingConfig.mobileLogoDark),
          favicon: resolveImagePath(brandingConfig.favicon),
          faviconDark: resolveImagePath(brandingConfig.faviconDark),
          loginBackgroundImage: resolveImagePath(brandingConfig.loginBackgroundImage),
          loginLogo: resolveImagePath(brandingConfig.loginLogo),
        };

        setBrandingData({
          ...data,
          brandingConfig: resolvedConfig
        });

        // Apply branding to document
        applyBrandingToDocument(resolvedConfig);
      } else {
        throw new Error(data.error || 'Failed to load branding configuration');
      }
    } catch (err) {
      console.error('Error fetching branding:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');

      // Apply default branding on error
      const defaultBranding: BrandingConfig = {
        appName: 'Ring2All Platform',
        appDescription: 'Multi-tenant communication platform',
        welcomeMessage: 'Welcome to Ring2All Platform',
        footerText: '© 2026 Ring2All LLC. All rights reserved.',
        primaryColor: '#3B82F6',
        secondaryColor: '#64748B',
        logo: null,
        favicon: null,
        loginBackgroundColor: '#ffffff',
        loginFontColor: '#ffffff',
        loginFormBackground: '#4448476B',
        loginSubmitButtonColor: '#202C2C7D',
        loginAddonsMenuColor: '#24363656',
        disableLoginBackgroundImage: false,
        loginBackgroundImage: null,
        loginLogo: null,
      };

      setBrandingData({
        tenant: { id: 'main', name: 'Main', slug: 'main' },
        brandingConfig: defaultBranding
      });

      applyBrandingToDocument(defaultBranding);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Apply branding configuration to document
  const applyBrandingToDocument = useCallback((config: BrandingConfig) => {
    if (typeof document === 'undefined') return;

    // Set page title
    if (config.appName) {
      document.title = config.appName;
    }

    // Set favicon
    if (config.favicon) {
      // Force the browser to refresh the favicon by appending a cache-buster timestamp.
      // Chrome's Favicon Service ignores standard DevTools "Disable Cache" options!
      const faviconUrl = config.favicon + (config.favicon.includes('?') ? '&' : '?') + 'v=' + Date.now();
      
      const existingFavicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (existingFavicon) {
        existingFavicon.href = faviconUrl;
      } else {
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = faviconUrl;
        document.head.appendChild(favicon);
      }
    }

    // Apply CSS custom properties for theming
    const root = document.documentElement;
    root.style.setProperty('--branding-primary', config.primaryColor);
    root.style.setProperty('--branding-secondary', config.secondaryColor);
    root.style.setProperty('--branding-login-bg', config.loginBackgroundColor || '#ffffff');
    root.style.setProperty('--branding-login-font', config.loginFontColor || '#ffffff');
    root.style.setProperty('--branding-login-form-bg', config.loginFormBackground || '#4448476B');
    root.style.setProperty('--branding-login-button', config.loginSubmitButtonColor || '#202C2C7D');
    root.style.setProperty('--branding-login-menu', config.loginAddonsMenuColor || '#24363656');
  }, []);

  // Load branding on mount
  useEffect(() => {
    const domain = getCurrentDomain();
    fetchBranding(domain);
  }, [getCurrentDomain, fetchBranding]);

  return {
    brandingData,
    isLoading,
    error,
    refetch: () => {
      const domain = getCurrentDomain();
      fetchBranding(domain);
    }
  };
};
